import { useState, useCallback, useEffect } from "react";
import { useAtomValue } from "jotai";
import {
  ModalDialogBody,
  ModalDialogHeader,
  ModalDialogRoot,
  Spinner,
  Text,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { togglesAtomResettable } from "@zapier/canvas-state";
import { toEmbeddableUrl } from "../../../utils/toEmbeddableUrl";
import { isImageUrl } from "@zapier/rich-text-editor";
import { getSandboxAttribute } from "../../../utils/getSandboxAttribute";
import { getCurrentOrigin } from "../../../utils/getCurrentOrigin";
import { VideoPlayer, isValidVideoUrl } from "../VideoPlayer";
import { LinkImage } from "../LinkImage";
import styles from "./EmbedContentModal.module.css";

type Props = {
  url: string;
  isOpen: boolean;
  onClose: () => void;
};

export function EmbedContentModal({ url, isOpen, onClose }: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const embeddableUrl = toEmbeddableUrl(url);
  const isImage = isImageUrl(embeddableUrl);
  const isVideoUrl = isValidVideoUrl(embeddableUrl);

  const { trusted_embed_domains } = useAtomValue(togglesAtomResettable);
  const currentOrigin = getCurrentOrigin();

  const sandboxAttr = getSandboxAttribute(
    url,
    trusted_embed_domains.config.domains,
    currentOrigin,
  );

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleIframeError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  // Reset states when opening
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [isOpen]);

  const hostname = (() => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  })();

  return (
    <ModalDialogRoot
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      aria-label={`Embedded content from ${hostname}`}
      className={styles.modal}
    >
      <ModalDialogHeader>{hostname}</ModalDialogHeader>
      <ModalDialogBody className={styles.body}>
        {hasError ? (
          <div className={styles.errorContainer}>
            <Icon name="alertCircleFill" color="StrokeStronger" size={32} />
            <Text type="ParagraphHeader1" color="TextWeaker">
              Failed to load content
            </Text>
            <Text type="Body1" color="TextWeaker">
              This website may not allow embedding or there might be a
              connection issue.
            </Text>
          </div>
        ) : (
          <div className={styles.iframeContainer}>
            {isImage ? (
              <LinkImage url={embeddableUrl} variant="modal" />
            ) : isVideoUrl ? (
              <VideoPlayer url={embeddableUrl} />
            ) : (
              <>
                {isLoading && (
                  <div className={styles.loadingContainer}>
                    <Spinner />
                  </div>
                )}
                <iframe
                  src={embeddableUrl}
                  title="Embedded content"
                  className={styles.iframe}
                  sandbox={sandboxAttr}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                />
              </>
            )}
          </div>
        )}
      </ModalDialogBody>
    </ModalDialogRoot>
  );
}
